import Highcharts from 'highcharts/highstock';
import { color } from '@op/opds.tokens';
import { getChartOptionsWithOverrides } from '../../util/defaultOptions';
import { getUrlEncodedCircle, getUrlEncodedSquare } from '../../util/svgIcons';

const countProfitExceptationMonth = (profitExceptation12Month: number): number => {
  return (1 + profitExceptation12Month / 100) ** (1 / 12) - 1;
};

export const createSavingArray = (
  initialSaving: number,
  savingTime: number,
  monthlySaving: number,
  showMarker: boolean,
): Array<number | { y: number; marker: { enabled: boolean } }> => {
  let totalSavings = initialSaving;
  const result = [...Array(savingTime * 12 + 1)].map((_, index) => {
    switch (index) {
      case 0:
        return { y: initialSaving, marker: { enabled: showMarker } };
      default:
        totalSavings += monthlySaving;
        if (index === savingTime * 12) {
          return { y: totalSavings, marker: { enabled: showMarker } };
        }
        return totalSavings;
    }
  });
  return result;
};

export const createProfitArray = (
  profitExpectation: number,
  initialSaving: number,
  savingTime: number,
  monthlySaving: number,
  showMarker: boolean,
): Array<number | { y: number; marker: { enabled: boolean } }> => {
  const kktuotto = countProfitExceptationMonth(profitExpectation);
  let finalSavingsAndProfit = initialSaving;
  const result = [...Array(savingTime * 12 + 1)].map((_, index) => {
    switch (index) {
      case 0:
        return { y: initialSaving, marker: { enabled: showMarker } };
      default:
        finalSavingsAndProfit += monthlySaving;
        finalSavingsAndProfit *= 1 + kktuotto;
        if (index === savingTime * 12) {
          return { y: finalSavingsAndProfit, marker: { enabled: showMarker } };
        }
        return finalSavingsAndProfit;
    }
  });
  return result;
};

export const interestGraphOptions = (
  profitData: Array<number | { y: number; marker: { enabled: boolean } }>,
  savingsData: Array<number | { y: number; marker: { enabled: boolean } }> = [],
): Object => {
  return getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: '',
    },
    chart: {
      marginBottom: 20,
      width: null,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        enableMouseTracking: false,
        marker: {
          enabled: false,
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      showLastLabel: true,
      showFirstLabel: true,
      opposite: false,
      labels: {
        enabled: true,
        allowOverlap: false,
        format: '{value:,.0f} €',
      },
    },
    xAxis: {
      type: 'datetime',
      tickInterval: 365 * 24 * 3600 * 1000 * 12,
      units: [['year', [1]]],
      lineWidth: 0,
      minorGridLineWidth: 0,
      minorTickLength: 0,
      tickLength: 0,
      labels: {
        enabled: false,
        allowOverlap: false,
      },
    },
    series: [
      {
        color: color.indicator.default,
        data: profitData,
        type: 'line',
        marker: {
          symbol: getUrlEncodedCircle(color.indicator.default),
        },
      },
      savingsData.length > 0 && {
        color: color.icon.default,
        data: savingsData,
        type: 'line',
        marker: {
          symbol: getUrlEncodedSquare(color.icon.default),
        },
      },
    ].filter(Boolean) as Highcharts.SeriesOptionsType[],
  });
};

export const msciGraphOptions = (data: number[], startDate: number): Object => {
  return getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: '',
    },
    chart: {
      height: 260,
      width: null,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        pointStart: startDate,
        pointInterval: (365 * 24 * 3600 * 1000) / 12,
        enableMouseTracking: false,
        marker: {
          enabled: false,
        },
      },
    },
    yAxis: {
      title: { text: undefined },
      showLastLabel: true,
      showFirstLabel: true,
      opposite: false,
      labels: {
        enabled: true,
        allowOverlap: false,
        formatter: (label: Highcharts.AxisLabelsFormatterContextObject) => {
          return label.value.toString();
        },
      },
    },
    xAxis: {
      type: 'datetime',
      lineWidth: 0,
      minorGridLineWidth: 0,
      minorTickLength: 0,
      tickLength: 0,
      tickInterval: 365 * 24 * 3600 * 1000 * 5,
      units: [['year', [5]]],
      labels: {
        enabled: true,
        allowOverlap: false,
      },
    },
    series: [
      {
        color: color.indicator.default,
        data,
        type: 'line',
        marker: {
          symbol: getUrlEncodedCircle(color.indicator.default),
        },
      },
    ],
  });
};

export const getPointDistribution = (
  data: {
    y: number;
    marker: { enabled: boolean };
  }[],
): Array<number> => {
  const result = [];
  const totalItems = data.length;
  const interval = totalItems / 10;
  for (let i = 0; i < 10; i += 1) {
    const evenIndex = Math.floor(i * interval + interval / 2);
    result.push(evenIndex);
  }

  return result;
};

const getAverageDataLine = (
  data: {
    y: number;
    marker: { enabled: boolean };
  }[],
): number => {
  const sum = Object.values(data).reduce((a, b) => {
    return a + b.y;
  }, 0);
  const avg = sum / data.length || 0;
  return avg;
};

export const dispersionGraphOptions = (
  data: { y: number; marker: { enabled: boolean } }[],
  startDate: number,
  averageArray: { y: number; marker: { enabled: boolean } }[],
): Object => {
  const avgData = getAverageDataLine(averageArray);
  return getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: '',
    },
    chart: {
      height: 260,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        pointStart: startDate || 1146441600000,
        pointInterval: (365 * 24 * 3600 * 1000) / 12,
        enableMouseTracking: false,
      },
    },
    yAxis: {
      title: { text: undefined },
      showLastLabel: true,
      showFirstLabel: true,
      opposite: false,
      labels: {
        enabled: false,
        allowOverlap: false,
      },
      plotLines: [
        {
          value: avgData,
          color: color.indicator.negative,
          width: 2,
        },
      ],
    },
    xAxis: {
      type: 'datetime',
      lineWidth: 0,
      minorGridLineWidth: 0,
      minorTickLength: 0,
      tickLength: 0,
      tickInterval: 365 * 24 * 3600 * 1000 * 12,
      units: [['year', [1, 5]]],
      labels: {
        enabled: false,
        allowOverlap: false,
      },
    },
    series: [
      {
        color: color.indicator.notification,
        data,
        type: 'line',
        marker: {
          symbol: getUrlEncodedCircle(color.indicator.default, '0.75rem'),
        },
      },
    ],
  });
};
